import * as polyclip from "polyclip-ts"

// Pure geometry for takeoff math. No React, no browser APIs — this module is
// shared with React Native later and is pinned by golden-fixture tests.

export type Pt = [number, number]
export type Ring = Pt[]

/** Signed shoelace area (world px^2); positive for CCW. */
export function shoelace(ring: Ring): number {
  let s = 0
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i]
    const [x2, y2] = ring[(i + 1) % ring.length]
    s += x1 * y2 - x2 * y1
  }
  return s / 2
}

export function ringPerimeter(ring: Ring): number {
  let p = 0
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i]
    const [x2, y2] = ring[(i + 1) % ring.length]
    p += Math.hypot(x2 - x1, y2 - y1)
  }
  return p
}

export type NetResult = { areaPx2: number; outerPerimPx: number }

/** Net polygon = outline minus holes (robust boolean via polyclip-ts). */
export function netPolygon(outline: Ring, holes: Ring[]): NetResult {
  if (outline.length < 3) return { areaPx2: 0, outerPerimPx: 0 }
  let geom: polyclip.Geom = [[outline]]
  for (const h of holes) {
    if (h.length < 3) continue
    geom = polyclip.difference(geom, [[h]])
  }
  let area = 0
  let outerPerim = 0
  const mp = geom as unknown as Ring[][]
  for (const poly of mp) {
    poly.forEach((ring, idx) => {
      const a = Math.abs(shoelace(ring))
      if (idx === 0) {
        area += a
        outerPerim += ringPerimeter(ring)
      } else {
        area -= a
      }
    })
  }
  return { areaPx2: area, outerPerimPx: outerPerim }
}

/**
 * Net polygon as polyclip returns it: one entry per disjoint piece, each
 * `[outerRing, ...holeRings]`. Keeping the grouping matters when the two get
 * different treatment — the room border is the product's color, deduction
 * borders are neutral — and the raw `holes` can't stand in for the hole rings,
 * since a deduction straddling a wall is clipped into the outer ring instead.
 */
export function netPolys(outline: Ring, holes: Ring[]): Ring[][] {
  if (outline.length < 3) return []
  let geom: polyclip.Geom = [[outline]]
  for (const h of holes) {
    if (h.length < 3) continue
    geom = polyclip.difference(geom, [[h]])
  }
  return geom as unknown as Ring[][]
}

/** All rings of the net polygon (outers + holes), for even-odd rendering. */
export function netRings(outline: Ring, holes: Ring[]): Ring[] {
  const rings: Ring[] = []
  for (const poly of netPolys(outline, holes)) for (const ring of poly) rings.push(ring)
  return rings
}

export type HoleMetrics = { areaPx2: number; perimPx: number; count: number }

/**
 * Area, perimeter, and count of the net polygon's INTERIOR holes — the basis
 * for the hole_area / hole_perimeter add-on methods (an island's border tile,
 * sealant around a hearth).
 *
 * Deliberately measured on the CLIPPED hole rings, not the raw `holes`: a
 * deduction straddling a wall folds into the outer ring, so it has no
 * interior perimeter and is not a hole here — its area already reduced net,
 * and there is no island to trim around. The zero-result surfacing names
 * this ("no interior deductions") rather than silently skipping.
 */
export function holeMetrics(outline: Ring, holes: Ring[]): HoleMetrics {
  let areaPx2 = 0
  let perimPx = 0
  let count = 0
  for (const poly of netPolys(outline, holes)) {
    for (let i = 1; i < poly.length; i++) {
      areaPx2 += Math.abs(shoelace(poly[i]))
      perimPx += ringPerimeter(poly[i])
      count += 1
    }
  }
  return { areaPx2, perimPx, count }
}

/** Ray-cast point-in-polygon (PNPOLY): toggles on each crossing. */
export function pointInRing(pt: Pt, ring: Ring): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    if (yi > pt[1] !== yj > pt[1] && pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi)
      inside = !inside
  }
  return inside
}

/** Closest point on segment ab to p, with its param t in [0, 1]. */
export function closestPointOnSegment(p: Pt, a: Pt, b: Pt): { pt: Pt; t: number } {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return { pt: a, t: 0 }
  const t = Math.min(1, Math.max(0, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq))
  return { pt: [ a[0] + dx * t, a[1] + dy * t ], t }
}

/**
 * Open a closed ring into a polyline by removing one of the two walls meeting
 * at corner `index`. All corners survive; the result always ENDS at the
 * selected corner (the active tracing end), and its first point is the
 * far side of the opened wall (the close target).
 *
 * - "next": removes the wall from ring[index] to its successor — the result
 *   walks forward from the successor around to ring[index].
 * - "prev": removes the wall from the predecessor to ring[index] — the
 *   result walks backward from the predecessor around to ring[index].
 */
export function openRing(ring: Ring, index: number, side: "prev" | "next"): Ring {
  const n = ring.length
  if (n < 3 || index < 0 || index >= n) return [ ...ring ]
  const out: Ring = []
  if (side === "next") {
    for (let k = 1; k <= n; k++) out.push(ring[(index + k) % n])
  } else {
    for (let k = 1; k <= n; k++) out.push(ring[(index - k + n) % n])
  }
  return out
}

export function centroid(ring: Ring): Pt {
  let x = 0,
    y = 0
  for (const [px, py] of ring) {
    x += px
    y += py
  }
  return [x / ring.length, y / ring.length]
}

/** Parse feet-inches: `25`, `25.5`, `25'`, `25'6`, `25'-6"`, `25' 6 1/2"` -> feet. */
export function parseFtIn(s: string): number | null {
  const t = s.trim().replace(/[""]/g, '"').replace(/['']/g, "'")
  if (!t) return null
  const m = t.match(
    /^(\d+(?:\.\d+)?)\s*(?:'|ft)?\s*(?:-|\s)?\s*(?:(\d+(?:\.\d+)?)\s*(?:(\d+)\s*\/\s*(\d+))?\s*"?)?$/
  )
  if (!m) return null
  const ft = parseFloat(m[1])
  const inches = m[2] ? parseFloat(m[2]) : 0
  const frac = m[3] && m[4] ? parseInt(m[3]) / parseInt(m[4]) : 0
  // bare decimal like `25.5` (no quote, no inches part) = feet
  if (!t.includes("'") && !m[2]) return ft
  return ft + (inches + frac) / 12
}

export function fmtFt(ft: number): string {
  const whole = Math.floor(ft)
  const inches = Math.round((ft - whole) * 12)
  if (inches === 12) return `${whole + 1}'-0"`
  return `${whole}'-${inches}"`
}

/** Translate every point of a ring by (dx, dy). */
/**
 * The axis-aligned rectangle between two diagonal corners, as a 4-point ring
 * STARTING at `a` — the drag-to-rectangle trace gesture's geometry. Starting
 * at the anchor keeps outline[0] (the seam/pattern anchor) on the corner the
 * human pressed first. Null when the corners share an x or y — a degenerate
 * rectangle is a line, and callers treat that as "this was just a click."
 */
export function rectRing(a: Pt, b: Pt): Ring | null {
  if (a[0] === b[0] || a[1] === b[1]) return null
  return [a, [b[0], a[1]], b, [a[0], b[1]]]
}

export function translateRing(ring: Ring, dx: number, dy: number): Ring {
  return ring.map(([x, y]): Pt => [x + dx, y + dy])
}

/** Translate the two endpoints of edge `edgeIndex` (wrapping) by (dx, dy). */
export function translateEdgePoints(ring: Ring, edgeIndex: number, dx: number, dy: number): Ring {
  const j = (edgeIndex + 1) % ring.length
  return ring.map((v, i): Pt => (i === edgeIndex || i === j ? [v[0] + dx, v[1] + dy] : v))
}
